import React, { useCallback, useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import { CheckboxWrapper, GetColor, Headline3 } from 'venn-ui-kit';
import {
  StyledLabel,
  DelimitedWrapper,
  type ImageProps,
  Field,
  InfoIcon,
  Img,
  getHideToggleConfig,
  GapWrapper,
} from '../shared';
import { StudioPrintSettingsContext, UserContext } from 'venn-components';
import { useHasFF } from 'venn-utils';
import { validate as uuidValidate } from 'uuid';
import {
  DEFAULT_STOCK_PHOTO,
  viewCoverPageImage,
  studioTitlePageHideCompanyLogo,
  studioTitlePageHasDisclosureTextAlignedRight,
  studioTitlePageHasFullPageBackgroundImage,
} from 'venn-state';
import { useRecoilState, useRecoilValue } from 'recoil';
import { BackgroundImageGrid, PageWithBackgroundImage } from './BackgroundImageGrid';
import { Tooltip, TooltipTrigger, TooltipPortal, TooltipContentWithArrow } from '@/components/ui/tooltip';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

const TitlePageSettingsSection = () => {
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');
  const { hasPermission } = useContext(UserContext);
  const { defaultLogoId } = useContext(StudioPrintSettingsContext);
  const coverPageImage = useRecoilValue(viewCoverPageImage);
  const [hasDisclosureTextAlignedRight, setStudioTitlePageHasDisclosureTextAlignedRight] = useRecoilState(
    studioTitlePageHasDisclosureTextAlignedRight,
  );
  const [hasFullPageBackgroundImage, setStudioTitlePageHasFullPageBackgroundImage] = useRecoilState(
    studioTitlePageHasFullPageBackgroundImage,
  );
  const hideCompanyLogoState = useRecoilState(studioTitlePageHideCompanyLogo);

  const canCreateTemplate = hasPermission('STUDIO_CREATE_TEMPLATE');
  const coverPageImageId = uuidValidate(coverPageImage) ? coverPageImage : DEFAULT_STOCK_PHOTO;
  const getImageURL = (imageId: string) => `/api/images/${imageId}`;
  const theme = useContext(ThemeContext);

  const Wrapper = hasCustomizationEnhancements ? DelimitedWrapper : GapWrapper;

  const onDisclosureTextAlignmentChange = useCallback(
    (value: string) => setStudioTitlePageHasDisclosureTextAlignedRight(value === 'right'),
    [setStudioTitlePageHasDisclosureTextAlignedRight],
  );

  const onFullPageBackgroundSettingChange = useCallback(
    (value: string) => setStudioTitlePageHasFullPageBackgroundImage(value === 'title-full-page-background'),
    [setStudioTitlePageHasFullPageBackgroundImage],
  );

  const expandedImageSettings = hasCustomizationEnhancements && (
    <div className="flex flex-col flex-[0_0_50%] gap-[12px]" style={{ flexBasis: 'calc(50% - 1.25rem)' }}>
      <div className="flex flex-col gap-[6px]">
        <StyledLabel htmlFor="title-page-image-settings">Image Mode</StyledLabel>
        <RadioGroup onValueChange={onFullPageBackgroundSettingChange} className="flex-col gap-[12px]">
          <div className="flex gap-[4px]">
            <RadioGroupItem
              value="title-cropped"
              id="title-cropped"
              state={hasFullPageBackgroundImage ? 'unchecked' : 'checked'}
            />
            <Label htmlFor="title-cropped" className="text-[12px] leading-[14px]">
              Cropped
            </Label>
          </div>
          <div className="flex gap-[4px]">
            <RadioGroupItem
              value="title-full-page-background"
              id="title-full-page-background"
              state={hasFullPageBackgroundImage ? 'checked' : 'unchecked'}
            />
            <Label htmlFor="title-full-page-background" className="text-[12px] leading-[14px]">
              Full Page Background Image
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon
                    type="info-circle"
                    prefix="far"
                    className={hasCustomizationEnhancements ? '!text-venn-grey-60' : ''}
                  />
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContentWithArrow>
                    When uploading a full page background image, your image must be: 1122 &times; 791px for Landscape or
                    791 &times; 1122px for Portrait (JPG, PNG).
                  </TooltipContentWithArrow>
                </TooltipPortal>
              </Tooltip>
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="flex flex-col gap-[6px]">
        <StyledLabel htmlFor="disclosure-text-settings">
          Disclosure Text Position
          <Tooltip>
            <TooltipTrigger>
              <InfoIcon
                type="info-circle"
                prefix="far"
                className={hasCustomizationEnhancements ? '!text-venn-grey-60' : ''}
              />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContentWithArrow>
                Company logo (if included) will be positioned opposite the disclosure text.
              </TooltipContentWithArrow>
            </TooltipPortal>
          </Tooltip>
        </StyledLabel>
        <RadioGroup onValueChange={onDisclosureTextAlignmentChange} className="flex-col gap-[12px]">
          <div className="flex gap-[4px]">
            <RadioGroupItem value="left" id="left" state={hasDisclosureTextAlignedRight ? 'unchecked' : 'checked'} />
            <Label htmlFor="left" className="text-[12px] leading-[14px]">
              Left
            </Label>
          </div>
          <div className="flex gap-[4px]">
            <RadioGroupItem value="right" id="right" state={hasDisclosureTextAlignedRight ? 'checked' : 'unchecked'} />
            <Label htmlFor="right" className="text-[12px] leading-[14px]">
              Right
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="flex flex-col gap-[6px]">
        <CheckboxWrapper
          {...getHideToggleConfig(hideCompanyLogoState)}
          disabled={!canCreateTemplate}
          spanStyle={{ paddingLeft: '4px' }}
          inputColor={hasCustomizationEnhancements ? theme.Colors.DarkBlue : undefined}
        >
          <Field>
            Company Logo
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon
                  type="info-circle"
                  prefix="far"
                  className={hasCustomizationEnhancements ? '!text-venn-grey-60' : ''}
                />
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContentWithArrow>Logo can be updated in Settings</TooltipContentWithArrow>
              </TooltipPortal>
            </Tooltip>
            {hasCustomizationEnhancements ? '' : ':'}
            {defaultLogoId && (
              <div className="inline-block h-[20px] overflow-hidden">
                <Img src={`/api/printing/logo/${defaultLogoId}`} />
              </div>
            )}
          </Field>
        </CheckboxWrapper>
      </div>
    </div>
  );

  return (
    <Wrapper>
      {hasCustomizationEnhancements && <Headline3 className="mb-[8px]">Title Page Settings</Headline3>}
      <div className="flex gap-[30px]">
        <div
          className={`flex flex-col ${hasCustomizationEnhancements ? 'flex-[0_0_50%]' : 'flex-1'}`}
          style={{ flexBasis: hasCustomizationEnhancements ? 'calc(50% - 1.25rem)' : '50%' }}
        >
          <StyledLabel htmlFor="cover-page-image">Title Page Image</StyledLabel>
          {canCreateTemplate ? (
            <BackgroundImageGrid
              page={PageWithBackgroundImage.TITLE_PAGE}
              singleColumnView={hasCustomizationEnhancements}
              canCreateTemplate={canCreateTemplate}
            />
          ) : (
            <div
              className={`inline-block relative h-[100px] ${hasCustomizationEnhancements ? 'w-full' : 'w-[47%]'} 
                  ml-0 mr-[3%] my-[1.5%] order-1 bg-center bg-cover object-cover`}
            >
              <Image src={getImageURL(coverPageImageId)} selected disabled />
            </div>
          )}
        </div>
        {canCreateTemplate && expandedImageSettings}
      </div>
    </Wrapper>
  );
};

export default TitlePageSettingsSection;

const Image = styled.img<ImageProps>`
  top: 0;
  left: 0;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  border: ${(props) =>
    props.selected
      ? css`
          3px solid ${GetColor.Primary.Main}
        `
      : '0px'};
  background-color: lightblue;
  width: 100%;
  height: 100px;
  order: 1;
  background-position: center;
  background-size: cover;
  object-fit: cover;
`;
